import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, PhoneOff, X, Loader2, Volume2, CalendarCheck } from "lucide-react";
import { getCalApi } from "@calcom/embed-react";
import { DemoActionCard, makeDemoAction } from "@/components/DemoActionCard";
import { V } from "@/lib/vocab";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAL_LINK = "mdawson40/30min";
const CAL_NS = "30min";

export const VoiceDemo = ({ open, onClose }) => {
  const [status, setStatus] = useState("idle"); // idle | connecting | live | error
  const [community, setCommunity] = useState("");
  const [persona, setPersona] = useState("the concierge");
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const [actions, setActions] = useState([]);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const cfgRef = useRef(null);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/webdemo/config");
      cfgRef.current = data;
      setCommunity(data.community || "");
      setPersona(data.persona || "the concierge");
    } catch {}
  };

  const cleanup = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    streamRef.current = null;
    setSpeaking(false);
  };

  const hangup = () => { cleanup(); setStatus("idle"); setTranscript(""); setActions([]); onClose?.(); };

  const openBooking = async () => {
    try {
      const cal = await getCalApi({ namespace: CAL_NS });
      cal("ui", { hideEventTypeDetails: false, layout: "month_view", cssVarsPerTheme: { light: { "cal-brand": "#d32f2f" }, dark: { "cal-brand": "#d32f2f" } } });
      hangup();
      cal("modal", { calLink: CAL_LINK, config: { layout: "month_view" } });
    } catch (e) {}
  };

  const start = async () => {
    setError(""); setTranscript(""); setActions([]); setStatus("connecting");
    try {
      let cfg = cfgRef.current;
      if (!cfg) { const r = await api.get("/webdemo/config"); cfg = r.data; cfgRef.current = cfg; setCommunity(cfg.community); setPersona(cfg.persona); }

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      const audio = new Audio(); audio.autoplay = true; audio.setAttribute("playsinline", "true"); audioRef.current = audio;
      pc.ontrack = (e) => { audio.srcObject = e.streams[0]; audio.play().catch(() => {}); };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const dc = pc.createDataChannel("oai-events");
      dc.onopen = () => {
        dc.send(JSON.stringify({ type: "response.create" }));
      };
      dc.onmessage = (e) => {
        let evt; try { evt = JSON.parse(e.data); } catch { return; }
        if (evt.type === "response.audio_transcript.delta") { setSpeaking(true); setTranscript((p) => p + (evt.delta || "")); }
        else if (evt.type === "response.audio_transcript.done") { setSpeaking(false); }
        else if (evt.type === "response.created") { setTranscript(""); }
        else if (evt.type === "input_audio_buffer.speech_started") { setSpeaking(false); }
        else if (evt.type === "response.output_item.done" && evt.item?.type === "function_call") {
          const it = evt.item;
          let args = {}; try { args = JSON.parse(it.arguments || "{}"); } catch {}
          const act = makeDemoAction(it.name, args);
          if (act) { setActions((p) => [...p.slice(-2), act]); toast("⚡ Powered by Ask Redline", { description: `${act.title}${act.ref ? ` · ${act.ref}` : ""}`, duration: 3500 }); }
          try {
            dc.send(JSON.stringify({ type: "conversation.item.create", item: { type: "function_call_output", call_id: it.call_id, output: JSON.stringify({ success: true }) } }));
            dc.send(JSON.stringify({ type: "response.create" }));
          } catch {}
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const resp = await fetch(`${BACKEND}/api/webdemo/negotiate`, {
        method: "POST", body: offer.sdp, headers: { "Content-Type": "application/sdp" },
      });
      if (!resp.ok) throw new Error("Could not connect to the voice service.");
      const answer = await resp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
      setStatus("live");
    } catch (err) {
      cleanup();
      setError(err?.message?.includes("Permission") || err?.name === "NotAllowedError"
        ? "Microphone access is needed for the voice demo. Please allow it and try again."
        : (err?.message || "Something went wrong starting the demo."));
      setStatus("error");
    }
  };

  useEffect(() => { if (open) loadConfig(); else cleanup(); }, [open]);
  useEffect(() => () => cleanup(), []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="voice-demo-modal">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={hangup} />
      <div className="relative w-full max-w-md rounded-3xl bg-ink text-white overflow-hidden shadow-2xl">
        <div className="absolute -top-24 -right-24 h-72 w-72 bg-redline-500/25 blur-3xl rounded-full" />
        <button onClick={hangup} data-testid="voice-demo-close" className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"><X className="h-4 w-4" /></button>
        {(
        <div className="relative p-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-400 mb-1">Live voice demo</p>
          <h3 className="font-heading text-2xl font-extrabold">Talk to {persona}</h3>
          {community && <p className="text-sand-300 text-sm mt-1">{community}</p>}

          <div className="my-8 flex flex-col items-center">
            <div className={`relative h-28 w-28 rounded-full flex items-center justify-center transition-all ${status === "live" ? "bg-redline-500" : "bg-white/10"}`}>
              {status === "live" && <span className={`absolute inset-0 rounded-full bg-redline-500/40 ${speaking ? "animate-ping" : ""}`} />}
              {status === "connecting" ? <Loader2 className="h-10 w-10 animate-spin" />
                : status === "live" ? (speaking ? <Volume2 className="h-10 w-10 relative" /> : <Mic className="h-10 w-10 relative" />)
                : <Mic className="h-10 w-10" />}
            </div>
            <p className="mt-4 text-sm text-sand-300 min-h-[20px]">
              {status === "idle" && "Tap below and just start talking."}
              {status === "connecting" && "Connecting…"}
              {status === "live" && (speaking ? `${persona} is speaking…` : "Listening, go ahead and ask.")}
              {status === "error" && <span className="text-danger-300">{error}</span>}
            </p>
          </div>

          {transcript && status === "live" && (
            <div data-testid="voice-demo-transcript" className="mb-6 max-h-28 overflow-y-auto rounded-2xl bg-white/[0.06] border border-white/10 p-4 text-left text-sm text-sand-100">{transcript}</div>
          )}

          {actions.length > 0 && status === "live" && (
            <div data-testid="voice-demo-actions" className="mb-6 space-y-2 text-left">
              {actions.map((a, i) => <DemoActionCard key={`${a.kind}-${i}`} action={a} dark />)}
            </div>
          )}

          {status === "idle" || status === "error" ? (
            <button onClick={start} data-testid="voice-demo-start" className="w-full rounded-full bg-redline-500 hover:bg-redline-600 font-semibold py-3.5 flex items-center justify-center gap-2 transition-colors">
              <Mic className="h-4 w-4" /> Start talking
            </button>
          ) : (
            <button onClick={hangup} data-testid="voice-demo-end" className="w-full rounded-full bg-white/10 hover:bg-white/20 font-semibold py-3.5 flex items-center justify-center gap-2 transition-colors">
              <PhoneOff className="h-4 w-4" /> End demo
            </button>
          )}
          <button onClick={openBooking} data-testid="voice-demo-book" className="mt-3 w-full rounded-full border border-white/15 bg-white/5 hover:bg-white/10 font-semibold py-3 flex items-center justify-center gap-2 transition-colors text-sm">
            <CalendarCheck className="h-4 w-4 text-redline-400" /> Book a 30-min demo
          </button>
          <p className="mt-4 text-[11px] text-sand-400">A live AI demo grounded in this {V.lc("org")}'s documents. For emergencies, call 911.</p>
        </div>
        )}
      </div>
    </div>
  );
};
